import React, { useState, useRef, useEffect } from "react";
import {
  Modal,
  View,
  Text,
  TouchableOpacity,
  Image,
  FlatList,
  Animated,
  Dimensions,
} from "react-native";
import { LinearGradient } from "expo-linear-gradient";
import { AppColors } from "../../utility/AppColors";
import { AppFontStyle } from "../../styles/AppFontStyle";
import { AppImages } from "../../utility/AppImages";
import { useLiveStream } from "../../context/LiveStreamContext";
import { PanGestureHandler } from "react-native-gesture-handler";

const { height: screenHeight } = Dimensions.get("window");

interface MobileGiftBottomSheetProps {
  visible: boolean;
  onClose: () => void;
  giftData: any[];
  handleGiftSelect: (item: any, receiverId?: string | null) => void;
  onOpenTokenPurchase: () => void;
  player1?: { userID: string; name: string; image: string };
  player2?: { userID: string; name: string; image: string };
  isAllOrNothing?: boolean;
}

const MobileGiftBottomSheet: React.FC<MobileGiftBottomSheetProps> = ({
  visible,
  onClose,
  giftData,
  handleGiftSelect,
  onOpenTokenPurchase,
  player1,
  player2,
  isAllOrNothing = false,
}) => {
  const { userData, audienceData } = useLiveStream();
  const [selectedGiftId, setSelectedGiftId] = useState<string | null>(null);
  const [selectedRecipient, setSelectedRecipient] = useState<string>("both"); // "both", player1.userID, or player2.userID
  const slideAnim = useRef(new Animated.Value(screenHeight * 0.5)).current;

  const eventStatus = audienceData?.eventDetails?.status || "Upcoming";
  const isEventComplete = eventStatus === "complete";

  useEffect(() => {
    if (visible) {
      // Slide up when visible - slide to 0 to show the bottom sheet
      Animated.spring(slideAnim, {
        toValue: 0,
        useNativeDriver: true,
        tension: 65,
        friction: 10,
      }).start();
    } else {
      // Slide down when not visible - slide to full height to hide
      Animated.timing(slideAnim, {
        toValue: screenHeight * 0.5,
        duration: 250,
        useNativeDriver: true,
      }).start();
    }
  }, [visible]);

  const handleGestureEnd = (event: any) => {
    // Disabled swipe-to-close - only allow closing by clicking outside
    // This prevents accidental closure when scrolling
  };

  const renderGiftItem = ({ item, index }: { item: any; index: number }) => {
    const isSelected = item.id === selectedGiftId;
    return (
      <TouchableOpacity
        onPress={() => {
          setSelectedGiftId(item.id);
        }}
        style={{
          width: "23%",
          marginBottom: 15,
          borderColor: isSelected ? "#DC2626" : "transparent",
          borderWidth: isSelected ? 1 : 0,
          borderRadius: 4,
          backgroundColor: isSelected ? "rgba(220, 38, 38, 0.10)" : "transparent",
          overflow: "hidden",
        }}
      >
        <View style={{ padding: 12, alignItems: "center" }}>
          <Image
            source={item.gift}
            style={{ width: 40, height: 40, marginBottom: 8 }}
            resizeMode="contain"
          />
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: 4,
            }}
          >
            <Image
              source={AppImages.Ko_Token}
              style={{ width: 12, height: 12 }}
              resizeMode="contain"
            />
            <Text style={[AppFontStyle.BOLD_ANYBODY_12, { color: "white" }]}>
              {item.price}
            </Text>
          </View>
        </View>
      </TouchableOpacity>
    );
  };

  return (
    <Modal transparent visible={visible} animationType="none">
      <View style={{ flex: 1 }}>
        <TouchableOpacity
          style={{ flex: 1 }}
          onPress={onClose}
          activeOpacity={1}
        />

        <PanGestureHandler onHandlerStateChange={handleGestureEnd}>
          <Animated.View
            style={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              height: screenHeight * 0.5,
              borderTopLeftRadius: 8,
              borderTopRightRadius: 8,
              transform: [{ translateY: slideAnim }],
              overflow: "hidden",
            }}
          >
            <LinearGradient
              colors={["#1F2937", "#111827"]}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 1 }}
              style={{
                flex: 1,
                borderTopLeftRadius: 8,
                borderTopRightRadius: 8,
                borderWidth: 0.8,
                borderColor: "#374151",
              }}
            >
              {/* Handle */}
              <View
                style={{
                  width: 40,
                  height: 4,
                  backgroundColor: AppColors.koBrandLightGray,
                  alignSelf: "center",
                  marginTop: 8,
                  borderRadius: 2,
                }}
              />

              {/* Header - Token Balance Section */}
              <LinearGradient
                colors={["#1F2937", "#111827"]}
                start={{ x: 0, y: 0 }}
                end={{ x: 0, y: 1 }}
                style={{
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginHorizontal: 20,
                  marginTop: 16,
                  marginBottom: 16,
                  paddingHorizontal: 12,
                  height: 34,
                  borderRadius: 4,
                  borderWidth: 0.8,
                  borderColor: "#374151",
                }}
              >
                <View
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <Image
                    source={AppImages.Ko_Token}
                    style={{ height: 20, width: 20 }}
                    resizeMode="contain"
                  />
                  <Text
                    style={[
                      AppFontStyle.BOLD_ANYBODY_14,
                      { color: "white" },
                    ]}
                  >
                    {userData?.tokens ?? 0}
                  </Text>
                  <Text
                    style={[
                      AppFontStyle.REGULAR_ANYBODY_12,
                      { color: AppColors.koBrandLightGray },
                    ]}
                  >
                    Token Balance
                  </Text>
                </View>

                <TouchableOpacity
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 6,
                  }}
                  onPress={onOpenTokenPurchase}
                >
                  <Image
                    source={AppImages.Ko_Token}
                    style={{ height: 16, width: 16 }}
                    resizeMode="contain"
                  />
                  <Text
                    style={[AppFontStyle.BOLD_ANYBODY_12, { color: "white" }]}
                  >
                    Recharge
                  </Text>
                  <Image
                    source={AppImages.arrowRight}
                    style={{ height: 12, width: 12 }}
                    resizeMode="contain"
                  />
                </TouchableOpacity>
              </LinearGradient>

              {/* Gift Grid */}
              <View
                style={{
                  flex: 1,
                  paddingHorizontal: 20,
                  paddingTop: 20,
                  opacity: isEventComplete ? 0.5 : 1,
                }}
                pointerEvents={isEventComplete ? "none" : "auto"}
              >
                <FlatList
                  data={giftData}
                  renderItem={renderGiftItem}
                  numColumns={4}
                  showsVerticalScrollIndicator={false}
                  nestedScrollEnabled={true}
                  scrollEnabled={true}
                  contentContainerStyle={{
                    paddingBottom: selectedGiftId ? 120 : 20,
                  }}
                  columnWrapperStyle={{ justifyContent: "space-between" }}
                />
              </View>

              {/* Bottom Player Selection Section - Shows when gift is selected */}
              {selectedGiftId && !isAllOrNothing && player1 && player2 && (
                <LinearGradient
                  colors={["#1F2937", "#111827"]}
                  start={{ x: 0, y: 0 }}
                  end={{ x: 1, y: 1 }}
                  style={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: 62,
                    paddingHorizontal: 6,
                    paddingVertical: 6,
                    borderWidth: 0.8,
                    borderColor: "#374151",
                  }}
                >
                  <View
                    style={{
                      flexDirection: "row",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    {/* Players Group */}
                    <View
                      style={{
                        flexDirection: "row",
                        gap: 10,
                      }}
                    >
                      {/* Player 1 */}
                      <TouchableOpacity
                        style={{
                          width: 85,
                          height: 46,
                          paddingVertical: 1.467,
                          paddingHorizontal: 7.2,
                          flexDirection: "column",
                          justifyContent: "center",
                          alignItems: "center",
                          gap: 2.933,
                          borderRadius: 4,
                          backgroundColor:
                            selectedRecipient === player1.userID
                              ? "rgba(220, 38, 38, 0.10)"
                              : "transparent",
                          borderWidth: 1,
                          borderColor:
                            selectedRecipient === player1.userID
                              ? "#DC2626"
                              : "rgba(255, 255, 255, 0.2)",
                        }}
                        onPress={() => setSelectedRecipient(player1.userID)}
                      >
                        <View
                          style={{
                            width: 21,
                            height: 22,
                            borderRadius: 21,
                            overflow: "hidden",
                            backgroundColor: AppColors.white,
                          }}
                        >
                          <Image
                            source={
                              player1.image
                                ? { uri: player1.image }
                                : AppImages.Ellipse1
                            }
                            style={{
                              width: 21,
                              height: 22,
                            }}
                            resizeMode="cover"
                          />
                        </View>
                        <Text
                          style={[
                            AppFontStyle.BOLD_ANYBODY_10,
                            { color: "white", textAlign: "center" },
                          ]}
                          numberOfLines={1}
                        >
                          {player1.name}
                        </Text>
                      </TouchableOpacity>

                      {/* Player 2 */}
                      <TouchableOpacity
                        style={{
                          width: 85,
                          height: 46,
                          paddingVertical: 1.467,
                          paddingHorizontal: 7.2,
                          flexDirection: "column",
                          justifyContent: "center",
                          alignItems: "center",
                          gap: 2.933,
                          borderRadius: 4,
                          backgroundColor:
                            selectedRecipient === player2.userID
                              ? "rgba(220, 38, 38, 0.10)"
                              : "transparent",
                          borderWidth: 1,
                          borderColor:
                            selectedRecipient === player2.userID
                              ? "#DC2626"
                              : "rgba(255, 255, 255, 0.2)",
                        }}
                        onPress={() => setSelectedRecipient(player2.userID)}
                      >
                        <View
                          style={{
                            width: 21,
                            height: 22,
                            borderRadius: 21,
                            overflow: "hidden",
                            backgroundColor: AppColors.white,
                          }}
                        >
                          <Image
                            source={
                              player2.image
                                ? { uri: player2.image }
                                : AppImages.Ellipse1
                            }
                            style={{
                              width: 21,
                              height: 22,
                            }}
                            resizeMode="cover"
                          />
                        </View>
                        <Text
                          style={[
                            AppFontStyle.BOLD_ANYBODY_10,
                            { color: "white", textAlign: "center" },
                          ]}
                          numberOfLines={1}
                        >
                          {player2.name}
                        </Text>
                      </TouchableOpacity>
                    </View>

                    {/* Send Button */}
                    <TouchableOpacity
                      style={{
                        width: 70,
                        backgroundColor: AppColors.koBrandRed,
                        borderRadius: 8,
                        paddingVertical: 12,
                        alignItems: "center",
                        justifyContent: "center",
                        opacity:
                          selectedRecipient && selectedRecipient !== "both"
                            ? 1
                            : 0.5,
                      }}
                      disabled={
                        !selectedRecipient || selectedRecipient === "both"
                      }
                      onPress={() => {
                        const selectedGift = giftData.find(
                          (g) => g.id === selectedGiftId
                        );
                        if (
                          selectedGift &&
                          selectedRecipient &&
                          selectedRecipient !== "both"
                        ) {
                          handleGiftSelect(selectedGift, selectedRecipient);
                          // Reset selections and close modal to show animation
                          setSelectedGiftId(null);
                          setSelectedRecipient("both");
                          onClose();
                        }
                      }}
                    >
                      <Text
                        style={[
                          AppFontStyle.BOLD_ANYBODY_14,
                          { color: "white", fontSize: 15 },
                        ]}
                      >
                        Send
                      </Text>
                    </TouchableOpacity>
                  </View>
                </LinearGradient>
              )}

              {/* For All or Nothing events - send without player selection */}
              {selectedGiftId && isAllOrNothing && (
                <View
                  style={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    backgroundColor: "#111827",
                    borderTopWidth: 1,
                    borderTopColor: "rgba(255, 255, 255, 0.1)",
                    paddingHorizontal: 20,
                    paddingVertical: 16,
                  }}
                >
                  <TouchableOpacity
                    style={{
                      backgroundColor: AppColors.koBrandRed,
                      borderRadius: 8,
                      paddingVertical: 12,
                      alignItems: "center",
                    }}
                    onPress={() => {
                      const selectedGift = giftData.find(
                        (g) => g.id === selectedGiftId
                      );
                      if (selectedGift) {
                        handleGiftSelect(selectedGift, null);
                        // Reset selection and close modal to show animation
                        setSelectedGiftId(null);
                        onClose();
                      }
                    }}
                  >
                    <Text
                      style={[AppFontStyle.BOLD_ANYBODY_14, { color: "white" }]}
                    >
                      Send Gift
                    </Text>
                  </TouchableOpacity>
                </View>
              )}
            </LinearGradient>
          </Animated.View>
        </PanGestureHandler>
      </View>
    </Modal>
  );
};

export default MobileGiftBottomSheet;
